import { useState, useCallback, useRef } from "react";

/**
 * useDraggable
 *
 * Returns drag state + a mousedown handler to attach to the drag handle element.
 *
 * Usage:
 *   const { position, handleMouseDown, resetPosition } = useDraggable();
 *
 *   <DialogHeader onMouseDown={handleMouseDown} className="tw:cursor-move tw:select-none">
 *     ...
 *   </DialogHeader>
 *   <DialogContent style={{ transform: `translate(calc(-50% + ${position.x}px), calc(-50% + ${position.y}px))` }}>
 *     ...
 *   </DialogContent>
 */
export function useDraggable() {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const dragOrigin = useRef(null);

  const handleMouseDown = useCallback(
    (e) => {
      // Left button only; ignore clicks on buttons/inputs inside the header
      if (e.button !== 0) return;
      if (e.target.closest("button, input, select, textarea, a")) return;

      dragOrigin.current = {
        mouseX: e.clientX,
        mouseY: e.clientY,
        posX: position.x,
        posY: position.y,
      };

      const onMouseMove = (moveEvent) => {
        if (!dragOrigin.current) return;
        setPosition({
          x: dragOrigin.current.posX + (moveEvent.clientX - dragOrigin.current.mouseX),
          y: dragOrigin.current.posY + (moveEvent.clientY - dragOrigin.current.mouseY),
        });
      };

      const onMouseUp = () => {
        dragOrigin.current = null;
        document.removeEventListener("mousemove", onMouseMove);
        document.removeEventListener("mouseup", onMouseUp);
      };

      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);

      e.preventDefault();
    },
    [position.x, position.y]
  );

  const resetPosition = useCallback(() => {
    setPosition({ x: 0, y: 0 });
  }, []);

  return { position, handleMouseDown, resetPosition };
}
